import { compareSsCode } from './ssCodeSort';

/** Parse "15.2-1" or "9-1-2" → { prefix, suffix, suffixNum } */
export function parseUnitUniqueId(uniqueId, ssCode) {
  let s = String(uniqueId || '').trim();
  if (ssCode) {
    const norm = String(ssCode).replace(/^SS\s+/i, '').replace(/\//g, '').trim();
    const m = s.match(/^(.+)-(\d+)$/);
    if (m) {
      return {
        prefix: m[1],
        suffix: String(parseInt(m[2], 10)),
        suffixNum: parseInt(m[2], 10),
        full: s,
        ssBare: norm,
      };
    }
    return { prefix: norm || s, suffix: '', suffixNum: null, full: s, ssBare: norm };
  }
  s = s.replace(/^SS\s+/i, '');
  const m = s.match(/^(.+)-(\d+)$/);
  if (!m) {
    return { prefix: s, suffix: '', suffixNum: null, full: s, ssBare: s };
  }
  const suffixNum = parseInt(m[2], 10);
  return {
    prefix: m[1],
    suffix: String(suffixNum),
    suffixNum: Number.isNaN(suffixNum) ? null : suffixNum,
    full: s,
    ssBare: m[1],
  };
}

/** SS code without the "SS " prefix: "SS 15.2" → "15.2" */
export function ssCodeBare(ssCode) {
  let prefix = String(ssCode || '')
    .replace(/\//g, '.')
    .trim();
  while (/^SS\s+/i.test(prefix)) {
    prefix = prefix.replace(/^SS\s+/i, '').trim();
  }
  return prefix;
}

export function buildUnitUniqueId(prefix, suffix) {
  const p = String(prefix || '').trim();
  const n = parseInt(String(suffix).replace(/\D/g, ''), 10);
  if (!p || !n || n < 1) return '';
  return `${p}-${n}`;
}

/** Display on map: 15.2-1 or 9-1-2 (no leading zero on suffix) */
export function formatUnitIdShort(uniqueId, ssCode) {
  const { prefix, suffixNum } = parseUnitUniqueId(uniqueId, ssCode);
  if (suffixNum == null) return uniqueId || '';
  return `${prefix}-${suffixNum}`;
}

/** Sort units by SS code, then ID prefix, then numeric suffix (2 before 10). */
export function compareSsUnitsByNumber(a, b) {
  const codeCmp = compareSsCode(a?.ss_code, b?.ss_code);
  if (codeCmp !== 0) return codeCmp;
  const pa = parseUnitUniqueId(a?.unique_id, a?.ss_code);
  const pb = parseUnitUniqueId(b?.unique_id, b?.ss_code);
  const prefixCmp = String(pa.prefix || '').localeCompare(String(pb.prefix || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  if (prefixCmp !== 0) return prefixCmp;
  const aNum = pa.suffixNum == null ? Number.MAX_SAFE_INTEGER : pa.suffixNum;
  const bNum = pb.suffixNum == null ? Number.MAX_SAFE_INTEGER : pb.suffixNum;
  if (aNum !== bNum) return aNum - bNum;
  return String(a?.unique_id || '').localeCompare(String(b?.unique_id || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function sortSsUnitsByNumber(units) {
  if (!Array.isArray(units)) return [];
  return [...units].sort(compareSsUnitsByNumber);
}

/** True if unique_id is exactly prefix or prefix-n (9-3 or 9-3-1). Avoids 9-1 matching 9-10. */
export function unitMatchesTypePrefix(uniqueId, prefix) {
  const id = String(uniqueId || '').trim();
  const p = String(prefix || '').trim();
  if (!id || !p) return false;
  if (id.toLowerCase() === p.toLowerCase()) return true;
  const escaped = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped}-\\d+$`, 'i').test(id);
}

/** Units of the same equipment type (all use {typePrefix}-{n}). */
export function floorChildUnits(parentUnit, allUnits) {
  const parentTypeId = parentUnit?.equipment_type_id;
  const list = (Array.isArray(allUnits) ? allUnits : []).filter((u) => {
    if (!u || String(u.id) === String(parentUnit.id)) return false;
    if (parentTypeId && u.equipment_type_id) {
      return String(u.equipment_type_id) === String(parentTypeId);
    }
    const prefix = String(parentUnit?.unique_id || '').trim().replace(/-\d+$/, '');
    if (!prefix) return false;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`^${escaped}-\\d+$`, 'i').test(String(u.unique_id || '').trim());
  });
  return sortSsUnitsByNumber(list);
}

export function unitMatchesEquipmentType(unit, type) {
  if (!unit || !type) return false;
  if (unit.equipment_type_id && String(unit.equipment_type_id) === String(type.id)) return true;
  return unitMatchesTypePrefix(unit.unique_id, type.prefix);
}
export function nextAutoUniqueIdForPrefix(prefix, units) {
  const p = String(prefix || '').trim();
  if (!p) return '';
  const used = new Set();
  const escaped = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^${escaped}-(\\d+)$`, 'i');
  (Array.isArray(units) ? units : []).forEach((u) => {
    const status = String(u?.status || 'ACTIVE').toUpperCase();
    if (status === 'INACTIVE' || status === 'PURGED') return;
    const unique = String(u?.unique_id || '').trim();
    if (unique.startsWith('__')) return;
    const m = unique.match(re);
    if (!m) return;
    used.add(parseInt(m[1], 10) || 0);
  });
  let n = 1;
  while (used.has(n)) n += 1;
  return `${p}-${n}`;
}

/** Next auto ID for an SS code given existing 2-part units (15.2-1 → 15.2-2). Ignores 9-1-2. */
export function nextAutoUniqueId(ssCode, units) {
  const prefix = ssCodeBare(ssCode);
  if (!prefix) return '';
  const used = new Set();
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const exact = new RegExp(`^${escaped}-(\\d+)$`, 'i');
  (Array.isArray(units) ? units : []).forEach((u) => {
    if (!u) return;
    const status = String(u?.status || 'ACTIVE').toUpperCase();
    if (status === 'INACTIVE' || status === 'PURGED') return;
    const unique = String(u.unique_id || '').trim();
    if (unique.startsWith('__')) return;
    const m = unique.match(exact);
    if (m) used.add(parseInt(m[1], 10) || 0);
  });
  let n = 1;
  while (used.has(n)) n += 1;
  return `${prefix}-${n}`;
}

export function isSuffixTaken(prefix, suffix, usedIds, currentId) {
  const candidate = buildUnitUniqueId(prefix, suffix);
  if (!candidate) return true;
  const norm = (id) => formatUnitIdShort(id).toLowerCase();
  const target = norm(candidate);
  return usedIds.some((id) => id !== currentId && norm(id) === target);
}
